import { defineComponent, computed, ref, reactive, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useCashierSettingsStore } from '@/store/cashier-settings/cashier-settings.store';
import { useCurrencyListStore } from '@/store/dictionary/currency-list.store';
import { Plus, ArrowRight, Delete } from '@element-plus/icons-vue';
import { CashierSettingsButtonName } from '@/models/cashier-settings/cashier-settings.model';
import type { CurrencyItem } from '@/models/dictionary/currency-list.model';
import { type FormRules, ElNotification, ElMessageBox } from 'element-plus';
import {
  type CurrencyFormValue,
  type SettingsForm,
  initSettingsFormSwitchers,
  initSettingsForm,
  changeableFields,
} from '@/models/cashier-settings/settings-screen.model';
import { cashierSettingsValidationRules } from '@/hooks/cashier-settings/cashier-settings-validation-rules';
import { getMappedFormDataFromConfig, getMappedPayload } from '@/utils/cashier-settings';
import AddCurrencyDialog from '@/layouts/cashier-settings-page/modals/add-currency-dialog/add-currency-dialog.vue';
import CurrencyForm from '@/layouts/cashier-settings-page/currency-form/currency-form.vue';
import SettingsScreenHeader
  from '@/layouts/cashier-settings-page/settings-screen/settings-screen-header/settings-screen-header.vue';
import { TagComponentTypes } from '@/models/app-common.model';

const icons = {
  Plus,
  ArrowRight,
  Delete,
};

export default defineComponent({
  name: 'SettingsScreen',
  components: {
    AddCurrencyDialog,
    CurrencyForm,
    SettingsScreenHeader,
  },
  setup() {
    const cashierSettingsStore = useCashierSettingsStore();
    const {
      selectedCountryConfig,
      isCashierConfigListLoading,
      isCurrencyFormChanged,
    } = storeToRefs(cashierSettingsStore);
    const {
      setSelectedCountry,
      getCashierConfigList,
      updateCashierConfig,
      setCashierConfigLoading,
      getCurrencyListFromConfig,
      getCashierConfig,
      setCurrencyFormChanged,
      resetSelectedCountryConfig,
    } = cashierSettingsStore;

    const { currencyList } = storeToRefs(useCurrencyListStore());

    const isAddCurrencyDialogVisible = ref(false);
    const currentCountryCurrencyList = ref<string[]>([]);
    const activeCollapseItems = ref<string[]>([]);
    const currencyFormList = ref<Record<string, CurrencyFormValue>>({});
    const recommendedMethodCount = ref<number>(
      selectedCountryConfig.value?.config?.recommendedMethods ?? 0,
    );

    const normalizeRecommendedMethodValue = (val: unknown): number => {
      if (val === null || val === undefined || val === '') {
        return 0;
      }
      if (typeof val === 'number' && !Number.isNaN(val)) {
        return val;
      }

      return 0;
    };

    const isRecommendedMethodCountChanged = computed<boolean>(() => {
      const current = normalizeRecommendedMethodValue(recommendedMethodCount.value);
      const original = normalizeRecommendedMethodValue(selectedCountryConfig.value?.config?.recommendedMethods ?? 0);

      return current !== original;
    });

    const currencyFormListRules = reactive<FormRules<SettingsForm>>(
      Object.keys(initSettingsForm).reduce((acc, field) => {
        if (changeableFields.includes(field)) {
          return acc;
        }

        return {
          ...acc,
          [field]: [cashierSettingsValidationRules.commonFieldRule],
        };
      }, {}),
    );

    const openAddCurrencyDialog = (): void => {
      isAddCurrencyDialogVisible.value = true;
    };

    const selectedCountryName = computed<string>(() => {
      return selectedCountryConfig.value.config?.countryName ?? '';
    });

    const isSaveButtonDisabled = computed<boolean>(() => !Object.keys(currencyFormList.value).length);

    const isCurrencyFormListEmpty = computed<boolean>(() => Object.keys(currencyFormList.value).length === 0);

    const availableCurrencyList = computed<CurrencyItem[]>(() => currencyList.value
      .filter((currency: CurrencyItem) => !currentCountryCurrencyList.value.includes(currency.code)));

    const handleCloseConfirmed = (): void => {
      setSelectedCountry('');
      getCashierConfigList();
      setCurrencyFormChanged(false);
      recommendedMethodCount.value = selectedCountryConfig.value?.config?.recommendedMethods ?? 0;
    };

    const closeSettingsScreen = (): void => {
      const onlyRecommendedMethodsChanged: boolean =
        isRecommendedMethodCountChanged.value &&
        !isCurrencyFormChanged.value;

      if (!isCurrencyFormChanged.value && !isRecommendedMethodCountChanged.value) {
        setSelectedCountry('');

        return;
      }

      if (onlyRecommendedMethodsChanged) {
        ElMessageBox.confirm(
          `You have unsaved changes.
          To save settings, you need to fill currency field. 
          Continue editing?
          `,
          `Close ${selectedCountryName.value} settings`,
          {
            distinguishCancelAndClose: true,
            confirmButtonText: 'Yes, continue',
            cancelButtonText: 'No, cancel',
            center: true,
            type: TagComponentTypes.WARNING,
          },
        ).catch((action: 'cancel' | 'close') => {
          if (action === 'cancel') {
            handleCloseConfirmed();
          }
        });

        return;
      }

      ElMessageBox.confirm(
        `Are you sure you want to close ${selectedCountryName.value} settings?
        If you made any changes, they will be lost without saving`,
        `Close ${selectedCountryName.value} settings?`,
        {
          distinguishCancelAndClose: true,
          confirmButtonText: 'Close settings',
          cancelButtonText: 'Cancel',
          center: true,
          type: TagComponentTypes.WARNING,
        },
      ).then(() => {
        handleCloseConfirmed();
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      }).catch(() => {});
    };

    const addCurrencies = (currencyList: string[]): void => {
      currencyList.forEach((currency) => {
        currencyFormList.value[currency] = {
          customRef: null,
          switchers: { ...initSettingsFormSwitchers },
          data: { ...initSettingsForm },
        };

        currentCountryCurrencyList.value.push(currency);
      });
    };

    const deleteCurrencyForm = (currency: string): void => {
      ElMessageBox.confirm(
        `Are you sure you want to delete ${currency} currency? All data will be lost`,
        `Delete ${currency}?`,
        {
          distinguishCancelAndClose: true,
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          center: true,
          type: 'error',
        },
      ).then(() => {
        const index = currentCountryCurrencyList.value.findIndex(currencyFromList => currencyFromList === currency);
        currentCountryCurrencyList.value.splice(index, 1);

        Reflect.deleteProperty(currencyFormList.value, currency);
        setCurrencyFormChanged(true);
      }).catch(error => {
        ElNotification({
          title: 'Error',
          message: error.message,
          type: 'error',
        });
      });
    };

    const submitForm = async (): Promise<void> => {
      const allFormsValidFlags: boolean[] = [];

      for (let i = 0; i < currentCountryCurrencyList.value.length; i++) {
        const currency = currentCountryCurrencyList.value[i];
        const formEl = currencyFormList.value[currency]?.customRef;

        if (!formEl) continue;

        changeableFields.forEach((key) => {
          if (!formEl?.switchers?.[key]) {
            currencyFormList.value[currency].data[key] = '';
          }
        });

        try {
          const valid = await formEl.currencyFormRef?.validate();
          allFormsValidFlags.push(valid);

          continue;
        } catch {
          allFormsValidFlags.push(false);

          ElNotification({
            title: 'Validation Error',
            message: `Validation failed for ${currency}.`,
            type: 'error',
          });

          continue;
        }
      }

      if (allFormsValidFlags.every(Boolean)) {
        setCurrencyFormChanged(false);

        const payload = getMappedPayload(currencyFormList.value, selectedCountryConfig.value.country);

        payload.recommendedMethods = recommendedMethodCount.value || 0;

        setCashierConfigLoading(true);

        updateCashierConfig(payload, selectedCountryName.value)
          .then(() => {
            return getCashierConfig(selectedCountryConfig.value.country);
          })
          .catch(() => {
            setCurrencyFormChanged(true);
          })
          .finally(() => {
            setCashierConfigLoading(false);
          });
      }
    };

    const initData = (): void => {
      recommendedMethodCount.value = selectedCountryConfig.value?.config?.recommendedMethods ?? 0;

      currentCountryCurrencyList.value =
        getCurrencyListFromConfig(selectedCountryConfig.value.config);

      activeCollapseItems.value = [...currentCountryCurrencyList.value];

      const formDataFromConfig = getMappedFormDataFromConfig(selectedCountryConfig.value.config);

      currencyFormList.value = currentCountryCurrencyList.value.reduce((acc, currency) => {
        const switchers = { ...initSettingsFormSwitchers };
        changeableFields.forEach(field => {
          switchers[field] = Boolean(formDataFromConfig?.[currency][field]);
        });

        return {
          ...acc,
          [currency]: {
            customRef: null,
            switchers,
            data: formDataFromConfig?.[currency] ?? { ...initSettingsForm },
          },
        };
      }, {});
    };

    onMounted(() => {
      getCashierConfig(selectedCountryConfig.value.country)
        .then(initData);
    });

    onUnmounted(() => {
      resetSelectedCountryConfig();
    });

    return {
      isCurrencyFormChanged,
      icons,
      submitForm,
      addCurrencies,
      currencyFormList,
      deleteCurrencyForm,
      closeSettingsScreen,
      selectedCountryName,
      isSaveButtonDisabled,
      availableCurrencyList,
      openAddCurrencyDialog,
      currencyFormListRules,
      CashierSettingsButtonName,
      isAddCurrencyDialogVisible,
      currentCountryCurrencyList,
      isCashierConfigListLoading,
      setCurrencyFormChanged,
      activeCollapseItems,
      isCurrencyFormListEmpty,
      recommendedMethodCount,
    };
  },
});
